"use client";
import type { Article } from "./types";
import { Children, ReactNode, isValidElement, type JSX } from "react";
import RelatedArticles from ".";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import MonarchResolver from "@/monarch/MonarchResolver";
import { LearnPageRedesignTestResult } from "@/monarch/rules/learnPageRedesign/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";

type ShortCodeRelatedArticlesProps = {
  heading?: string;
  "class-name"?: string;
  classname?: string;
  children?: JSX.Element;
};

export function ShortCodeRelatedArticles({
  heading,
  "class-name": className,
  classname,
  children,
}: ShortCodeRelatedArticlesProps) {
  const articles: Article[] = [];

  Children.toArray(children).forEach((child: ReactNode) => {
    if (isValidElement(child)) {
      const element = child as React.ReactElement<{
        title?: string;
        description?: string;
        author?: string;
        date?: string;
        url?: string;
      }>;

      // Handle article items
      if (
        element.props.title &&
        element.props.description &&
        element.props.author &&
        element.props.date
      ) {
        articles.push({
          title: element.props.title,
          description: element.props.description,
          author: element.props.author,
          date: element.props.date,
          url: element.props.url,
        });
      }
    }
  });

  // Use kebab-case version if available, otherwise fall back to lowercase version
  const finalClassName = className || classname;

  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_LEARN_PAGE_REDESIGN_TRAFFIC"
      variants={{
        legacy: null,
        test: (
          <MonarchResolver<LearnPageRedesignTestResult>
            experimentKey="useLearnPageRedesign"
            variants={{
              control: null,
              test: (
                <MonarchDataConsumer<LearnPageRedesignTestResult>
                  experimentKey="useLearnPageRedesign"
                  render={(testResult) => {
                    // Check if related articles are enabled in the experiment
                    if (
                      !testResult?.data?.relatedArticles ||
                      !testResult.data.relatedArticles.enabled
                    ) {
                      return null;
                    }
                    return (
                      <RelatedArticles
                        heading={heading}
                        articles={articles}
                        className={finalClassName}
                      />
                    );
                  }}
                />
              ),
            }}
            Loader={null}
            fallbackVariant="control"
          />
        ),
      }}
    />
  );
}

// This is a placeholder component that serves as a data carrier for the parent component
// It doesn't render anything - the parent extracts its props
export function ShortCodeRelatedArticlesItem() {
  return null;
}
